import { OrderType } from '@glow/entity-types'
import i18next from 'i18next'
import { fromJS, List, Map, Seq, Set } from 'immutable'
import isEqual from 'lodash-es/isEqual'
import replace from 'lodash-es/replace'
import split from 'lodash-es/split'
import toLower from 'lodash-es/toLower'
import toUpper from 'lodash-es/toUpper'
import trim from 'lodash-es/trim'
import { Customer } from '../../domain/customer'
import { convert, MeasurementUnit } from '../../domain/measurementUnits'
import { IOptimusService } from '../../domain/optimusService'
import { Consignment, DangerousGoods, Department, isHdDepartment, Order, Shipment } from '../../types/coreEntitiesTypes'
import { ImmutableMap, ImmutableMapFactory, isNotNullOrUndefined } from '../../types/immutableTypes'
import { isNotEmpty } from '../../utils/collectionUtils'
import { toLocaleDate } from '../../utils/dateTime'
import { isEmpty } from '../../utils/stringUtils'
import { isDeliveryOrderService } from './bookingFormikFormComponents/deliveryOrder'
import {
  AddressProps,
  DangerousGoodsFormikProps,
  IAddress,
  IMeasurement,
  INamedPriceListExplanation,
  IOrderForm,
  IOrderFormFormikValues,
  IOrderFormProps,
  IPriceExplanation,
  ISize,
  LatLng,
  newVAS,
  ValueAddedService
} from './bookingOrderTypes'
import { OrderAction } from './bookingSupport'
import { getAvailableMeasurements } from './measurements'
import { GroupRecipientProps } from './multistopTypes'

export const DEFAULT_SUBCUSTOMER = ''

const isImmutableRecord = (value: unknown): value is ImmutableMap<any> =>
  Boolean(value) && typeof (value as ImmutableMap<any>).get === 'function'

const getValue = (value: any, key: string): any => (isImmutableRecord(value) ? value.get(key) : value?.[key])

const toPlainArray = (value: any): any[] => {
  if (List.isList(value)) {
    return value.toArray()
  }

  return Array.isArray(value) ? value : []
}

// pure functions related to booking order types
function formatPriceExplanationsForCustomerOrResource(explanations: List<INamedPriceListExplanation>): string {
  return explanations
    .map((explanation) => {
      const priceList = explanation.get('priceList')
      const explanations = explanation.get('explanations')
      return (
        '' +
        (priceList
          ? '\t\t' + i18next.t('instant.booking.priceExplanations.priceListName', { pricelist: priceList }) + '\n'
          : '') +
        (explanations
          ? '\t\t' +
            i18next.t('instant.booking.priceExplanations.explanations') +
            ':' +
            '\n' +
            explanations.map((e) => `\t\t\t${e}`).join('\n')
          : '')
      )
    })
    .join('\n')
}

export function formatPriceExplanations(priceExplanations: List<IPriceExplanation>): string {
  return priceExplanations
    .map((e) => {
      const customerExplanations = e.get('customerExplanations')
      const resourceExplanations = e.get('resourceExplanations')
      return (
        i18next.t('instant.booking.priceExplanations.article', { article: e.get('article') }) +
        '\n\tCUSTOMER\n' +
        (customerExplanations && !customerExplanations.isEmpty()
          ? formatPriceExplanationsForCustomerOrResource(customerExplanations)
          : '\t\t' + i18next.t('instant.booking.priceExplanations.noInfo')) +
        '\n\tUNIT\n' +
        (resourceExplanations && !resourceExplanations.isEmpty()
          ? formatPriceExplanationsForCustomerOrResource(resourceExplanations)
          : '\t\t' + i18next.t('instant.booking.priceExplanations.noInfo'))
      )
    })
    .join('\n\n')
}

export const isPureVas = (vas: ValueAddedService) => vas.get('pureVas')

export const filterOrderCorrectionVas = (vas: ValueAddedService) =>
  !Set(['WAT PU', 'WAT DEL', 'LUT PU', 'LUT DEL']).contains(vas.get('vasCode', ''))

export function toPureVas(vas: ValueAddedService) {
  return newVAS(vas.get('vasCode'), vas.get('quantity') || '1', true)
}

export function emptyVAS() {
  return newVAS('', '')
}

export function ensureListWithOneItem<T>(list: List<T>, createIfEmpty: () => T): List<T> {
  const nonNull = list ? list : List<T>()
  return nonNull.isEmpty() ? List([createIfEmpty()]) : nonNull
}

export function pureVAS(service: IOptimusService) {
  return isEmpty(service.get('serviceCode')) && isNotNullOrUndefined(service.get('vasCode'))
}

export const getOrderType = (
  department: Department | undefined,
  service: IOptimusService | null | undefined,
  existingOrderIsHd: boolean
): OrderType | null => {
  const orderTypeFromService = service?.get('orderType')
  if (orderTypeFromService) {
    return orderTypeFromService
  } else if (isDeliveryOrderService(service)) {
    return 'Delivery'
  } else if ((department && isHdDepartment(department)) || existingOrderIsHd) {
    return 'HD'
  } else {
    return null
  }
}

export const getMessageSenderPartyId = (orderType: OrderType | null, baggageOrder: boolean): string => {
  if (orderType === 'HD') {
    return 'HappyFlow'
  } else if (baggageOrder) {
    return 'BaggageSolutions'
  } else {
    return 'glow'
  }
}

export const bookingFormatLocalDateTime = (date?: string, time?: string) => date && time && `${date}T${time}`

export const getMeasurementVolume = (measurement: IMeasurement): number => {
  const length = Number.parseInt(getValue(measurement, 'length'), 10)
  const width = Number.parseInt(getValue(measurement, 'width'), 10)
  const height = Number.parseInt(getValue(measurement, 'height'), 10)

  if (
    !(Number.isNaN(length) || Number.isNaN(width) || Number.isNaN(height)) &&
    (length > 0 || width > 0 || height > 0)
  ) {
    return (length * width * height) / 1000
  }

  const volume = Number.parseFloat(getValue(measurement, 'volume'))
  return Number.isNaN(volume) ? 0 : volume
}

type OrderFormPickupAdressCoordinate =
  | (ImmutableMap<Partial<Pick<IOrderFormProps, 'pickupAddressLat' | 'pickupAddressLng'>>> &
      Partial<Pick<IOrderFormProps, 'pickupAddressLat' | 'pickupAddressLng'>>)
  | Partial<Pick<IOrderFormFormikValues, 'pickupAddressLat' | 'pickupAddressLng'>>

function getPickupLatLng(values: OrderFormPickupAdressCoordinate) {
  return fromJS(
    getValue(values, 'pickupAddressLat') && getValue(values, 'pickupAddressLng')
      ? ({
          lat: getValue(values, 'pickupAddressLat'),
          lng: getValue(values, 'pickupAddressLng')
        } as LatLng)
      : undefined
  ) as ImmutableMap<LatLng> | undefined
}

function getLatLng(values: ImmutableMap<GroupRecipientProps>) {
  return fromJS(
    values.get('lat') && values.get('lng')
      ? ({
          lat: values.get('lat'),
          lng: values.get('lng')
        } as LatLng)
      : undefined
  ) as ImmutableMap<LatLng> | undefined | LatLng
}

export const createPickupAddress = (values: IOrderForm | IOrderFormFormikValues): IAddress => {
  return Map({
    name: getValue(values, 'pickupName'),
    address: getValue(values, 'pickupAddress'),
    zipArea: getValue(values, 'pickupZipArea'),
    zipCode: getValue(values, 'pickupZipCode'),
    country: getValue(values, 'pickupCountryCode'),
    phone: getValue(values, 'pickupPhone'),
    contactPerson: getValue(values, 'pickupContactPerson'),
    customerRef: getValue(values, 'customerRef'),
    instructions: getValue(values, 'pickupInstructions'),
    latLng: getPickupLatLng(values as any)
  }) as IAddress
}

export const createAddressFromRecipient = (values: ImmutableMap<GroupRecipientProps>): IAddress => {
  return ImmutableMapFactory<AddressProps>({
    name: values.get('name'),
    address: values.get('address'),
    zipArea: values.get('city'),
    zipCode: values.get('zipCode'),
    country: values.get('countryCode'),
    phone: values.get('phone'),
    contactPerson: values.get('contactPerson'),
    instructions: values.get('instructions'),
    latLng: getLatLng(values) as ImmutableMap<LatLng> & LatLng
  }) as IAddress
}

export const stripEmptyPhonePrefix = (phoneNumber: string, prefix: string) =>
  isEqual(trim(phoneNumber), prefix) ? '' : phoneNumber

export const getMeasurementsTotalVolume = (
  measurements: List<IMeasurement> | IOrderFormFormikValues['measurements']
): number =>
  toPlainArray(measurements)
    .map(getMeasurementVolume)
    .reduce((sum, val) => sum + val, 0)

export const getMeasurementsTotalHeight = (
  measurements: List<IMeasurement> | IOrderFormFormikValues['measurements']
): number => {
  const height = toPlainArray(measurements)
    .map((it) => Number.parseInt(getValue(it, 'height'), 10))
    .reduce((sum, val) => sum + val, 0)
  return isNaN(height) ? 0 : height
}

export const getMeasurementsTotalWidth = (
  measurements: List<IMeasurement> | IOrderFormFormikValues['measurements']
): number => {
  const width = toPlainArray(measurements)
    .map((it) => Number.parseInt(getValue(it, 'width'), 10))
    .reduce((sum, val) => sum + val, 0)
  return isNaN(width) ? 0 : width
}

export const getMeasurementsTotalLength = (
  measurements: List<IMeasurement> | IOrderFormFormikValues['measurements']
): number => {
  const length = toPlainArray(measurements)
    .map((it) => Number.parseInt(getValue(it, 'length'), 10))
    .reduce((sum, val) => sum + val, 0)
  return isNaN(length) ? 0 : length
}

export const getMeasurementsTotalWeight = (
  measurements: List<IMeasurement> | IOrderFormFormikValues['measurements']
): number => {
  const weight = toPlainArray(measurements)
    .map((m) => Number.parseFloat(getValue(m, 'weight')))
    .reduce((sum, val) => sum + val, 0)
  return isNaN(weight) ? 0 : weight
}

export const getMeasurementsTotalPalletFootPrints = (
  measurements: List<IMeasurement> | IOrderFormFormikValues['measurements']
): number => {
  const palletFootPrints = toPlainArray(measurements)
    .map((m) => Number.parseFloat(getValue(m, 'palletFootPrints') ?? '0'))
    .reduce((sum, val) => sum + val, 0)
  return isNaN(palletFootPrints) ? 0 : palletFootPrints
}

export const getMeasurementsTotalLoadingMeters = (
  measurements: List<IMeasurement> | IOrderFormFormikValues['measurements']
): number => {
  const loadingMeters = toPlainArray(measurements)
    .map((m) => Number.parseFloat(getValue(m, 'loadingMeters') ?? '0'))
    .reduce((sum, val) => sum + val, 0)
  return isNaN(loadingMeters) ? 0 : loadingMeters
}

export const shouldShowPalletIcon = (row: Shipment) => {
  if (!row || !row.has('consignments') || !row.get('consignments')) return false
  const presets = row && (row.get('consignments') || Map()).map((it: Consignment) => it.get('presetDimension')).toSet()
  return presets && !presets.intersect(Set(['fullpallet', 'halfpallet', 'quarterpallet', 'trolley'])).isEmpty()
}

export const shouldShowPalletIconOnWeightCheck = (row: Shipment) => {
  if (!row || !row.has('consignments') || !row.get('consignments')) return false
  return (
    row &&
    isNotEmpty(
      (row.get('consignments') || Map()).filter((it: Consignment) => {
        const consignmentWeight = weightInKGM(it.get('weightUnit') || MeasurementUnit.KGM, it.get('weightValue'))
        return consignmentWeight && consignmentWeight > 35
      })
    )
  )
}

export const weightInKGM = (consignmentWeightUnit: string, consignmentWeight: number) => {
  return convert(consignmentWeightUnit, MeasurementUnit.KGM, consignmentWeight)
}

export const vehicleType = List<string>(['bicycle', 'cargobike', 'motorcycle', 'car', 'van', 'truck'])

export const getOptimusXYValue = (param: string, zone: string, qty: number, weight: number): string | undefined => {
  switch (toUpper(param)) {
    case 'ZONE':
      return zone
    case 'QTY':
      return String(qty)
    case 'KG':
      return String(weight)
    default:
      return undefined
  }
}

export const getPhonePrefixFromDepartment = (department?: Department) => {
  switch (toLower(department?.get('country', ''))) {
    case 'fi':
      return '+358'
    case 'se':
      return '+46'
    case 'dk':
      return '+45'
    case 'no':
      return '+47'
    default:
      return ''
  }
}
export const replaceEveningAndWeight: (alystraServiceCode: string) => string = (alystraServiceCode: string) =>
  replace(alystraServiceCode, /({WEIGHT})|({EVENING})/, '')

export function formatPickupAddress(shipment: Shipment) {
  const address = shipment.get('pickupAddress') || ''
  const zip = shipment.get('pickupZipCode') || ''
  const city = shipment.get('pickupZipArea') || ''
  return `${address}, ${zip} ${city}`
}

export function formatDeliveryAddress(shipment: Shipment) {
  const address = shipment.get('deliveryAddress') || ''
  const zip = shipment.get('deliveryZipCode') || ''
  const city = shipment.get('deliveryZipArea') || ''
  return `${address}, ${zip} ${city}`
}

export const customerNumberAndSubcustomer = (customerNumber?: string, subCustomer?: string | null) =>
  customerNumber + (subCustomer ? '-' + subCustomer : '')

export const getCustomerSubCustomer = (value: string): { customerNumber: string; subcustomer?: string } => {
  const customerParts = split(value, /-(.*)/).map(trim)
  return {
    customerNumber: customerParts[0],
    subcustomer: customerParts.length > 1 ? customerParts[1] : undefined
  }
}

const joinNotEmpty = (parts: (string | undefined)[], separator: string) => {
  return parts.filter((p) => Boolean(p)).join(separator)
}

export const customerNameAndNumber = (
  name: string | null | undefined,
  customerNumber: string | null | undefined,
  subCustomer?: string | null
) =>
  joinNotEmpty(
    [name ?? undefined, customerNumber ? customerNumberAndSubcustomer(customerNumber, subCustomer) : undefined],
    ' - '
  )

// Customers that have been migrated from Alystra may have subcustomers wrapped in pipes, e.g. `alystra-id|subcustomer`
// since the subcustomer will already be part of the customer name of the migrated customer, we remove it from here
const removeWrappedSubcustomer = (wrapped: string) =>
  wrapped.includes('|') ? wrapped.substring(0, wrapped.indexOf('|')) : wrapped

export const getCustomerSuggestionLabel = (suggestion: any) =>
  joinNotEmpty(
    [
      suggestion.name,
      suggestion.orgNumber,
      joinNotEmpty([suggestion.alystraId, suggestion.alystraSubcustomer], '-'),
      suggestion.otherIdentifiers?.length &&
        joinNotEmpty(suggestion.otherIdentifiers.map(removeWrappedSubcustomer), ', '),
      joinNotEmpty([suggestion.address, joinNotEmpty([suggestion.zipCode, suggestion.zipArea], ' ')], ', '),
      suggestion.customerCode,
      suggestion.accountingTags?.length && joinNotEmpty(suggestion.accountingTags, ', '),
      suggestion.isInternalCustomer && i18next.t('customer.nonInvoiceable')
    ],
    ' - '
  )

export const getCustomerSuggestionLabelWithoutSubcustomer = (suggestion: any) =>
  joinNotEmpty(
    [
      suggestion.name,
      suggestion.orgNumber,
      suggestion.alystraId,
      suggestion.otherIdentifiers?.length &&
        joinNotEmpty(suggestion.otherIdentifiers.map(removeWrappedSubcustomer), ', '),
      joinNotEmpty([suggestion.address, joinNotEmpty([suggestion.zipCode, suggestion.zipArea], ' ')], ', '),
      suggestion.customerCode,
      suggestion.accountingTags?.length && joinNotEmpty(suggestion.accountingTags, ', '),
      suggestion.isInternalCustomer && i18next.t('customer.nonInvoiceable')
    ],
    ' - '
  )

export const getCustomerLabel = (v: { alystraId: string; alystraSubcustomer?: string }) =>
  customerNumberAndSubcustomer(v.alystraId, v.alystraSubcustomer)

export const getCustomerLabelWithoutSubcustomer = (v: { alystraId: string }) => v.alystraId

export const getDeliveryDateLatest = (deliveryTimeEarliest: string, deliveryTimeLatest: string, pickupDate: string) =>
  toLocaleDate(deliveryTimeEarliest) > toLocaleDate(deliveryTimeLatest)
    ? toLocaleDate(pickupDate).plus({ days: 1 }).toISODate()
    : pickupDate

export const shouldUpdateCustomerAddress = (formValues: IOrderForm | IOrderFormFormikValues, department: Department) =>
  !List.of<keyof IOrderFormProps>(
    'pickupName',
    'pickupAddress',
    'pickupZipCode',
    'pickupZipArea',
    'pickupCountryCode',
    'pickupPhone',
    'pickupContactPerson',
    'pickupInstructions'
  ).some((key) => {
    const value = getValue(formValues, key)
    const allowedPrefix = key === 'pickupPhone' ? getPhonePrefixFromDepartment(department) : ''
    return value !== null && value !== undefined && value.toString().length > 0 && value !== allowedPrefix
  })

export const hasPrice = (price: string) => Number(price) > 0

export const formatPrice = (price: string) => (hasPrice(price) ? price : '')

export const anyMandatorySubcustomers = (subcustomers: Map<string, any>): boolean =>
  subcustomers.valueSeq().some((subcustomer) => subcustomer.get('mandatorySubcustomer'))

export const filterSubcustomers = (subcustomers: Map<string, any>, phrase: string) => {
  const mandatorySubcustomers = anyMandatorySubcustomers(subcustomers)
  return isNotEmpty(subcustomers)
    ? subcustomers
        .valueSeq()
        .filter((subcustomer: Customer) => subcustomerMatchesPhrase(subcustomer, phrase))
        .filter((subcustomer: Customer) =>
          mandatorySubcustomers ? subcustomer.get('alystraSubcustomer') !== DEFAULT_SUBCUSTOMER : true
        )
        .sort(sortByKey('alystraSubcustomer', true))
        .toJS()
    : []
}

export const sortByKey = (key: string, isAscending: boolean) => (a: Map<string, any>, b: Map<string, any>) => {
  const first = a.get(key)
  const second = b.get(key)
  const result = first > second ? 1 : second > first ? -1 : 0
  return isAscending ? result : -result
}

export const subcustomerMatchesPhrase = (subcustomer: Customer, phrase: string) =>
  phrase ? subcustomer.get('alystraSubcustomer').toUpperCase().includes(phrase.toUpperCase()) : true

export const formValuesToGroupRecipient = (values: IOrderForm | IOrderFormFormikValues, phonePrefix?: string) => {
  const phone = phonePrefix
    ? stripEmptyPhonePrefix(getValue(values, 'deliveryPhone'), phonePrefix)
    : getValue(values, 'deliveryPhone')
  const secondPhone = getValue(values, 'deliverySecondPhone')
  const secondPhoneStripped = phonePrefix && secondPhone ? stripEmptyPhonePrefix(secondPhone, phonePrefix) : secondPhone

  return ImmutableMapFactory<GroupRecipientProps>({
    name: getValue(values, 'deliveryName'),
    address: getValue(values, 'deliveryAddress'),
    city: getValue(values, 'deliveryZipArea'),
    zipCode: trim(getValue(values, 'deliveryZipCode')),
    countryCode: getValue(values, 'deliveryCountryCode'),
    phone,
    secondPhone: secondPhoneStripped,
    contactPerson: getValue(values, 'deliveryContactPerson'),
    instructions: getValue(values, 'deliveryInstructions'),
    reference: getValue(values, 'recipientRef'),
    lat: getValue(values, 'deliveryAddressLat'),
    lng: getValue(values, 'deliveryAddressLng'),
    email: getValue(values, 'deliveryEmail'),
    resolvedBy: getValue(values, 'deliveryResolvedBy'),
    pupUnitId: getValue(values, 'deliveryPupUnitId') || null
  })
}

export const formValuesToGroupPickup = (values: IOrderForm | IOrderFormFormikValues, phonePrefix?: string) => {
  const phone = phonePrefix
    ? stripEmptyPhonePrefix(getValue(values, 'pickupPhone'), phonePrefix)
    : getValue(values, 'pickupPhone')
  const secondPhone = getValue(values, 'pickupSecondPhone')
  const secondPhoneStripped = phonePrefix && secondPhone ? stripEmptyPhonePrefix(secondPhone, phonePrefix) : secondPhone

  return ImmutableMapFactory<GroupRecipientProps>({
    name: getValue(values, 'pickupName'),
    address: getValue(values, 'pickupAddress'),
    city: getValue(values, 'pickupZipArea'),
    zipCode: trim(getValue(values, 'pickupZipCode')),
    countryCode: getValue(values, 'pickupCountryCode'),
    phone,
    secondPhone: secondPhoneStripped,
    contactPerson: getValue(values, 'pickupContactPerson'),
    instructions: getValue(values, 'pickupInstructions'),
    reference: getValue(values, 'customerRef'),
    lat: getValue(values, 'pickupAddressLat'),
    lng: getValue(values, 'pickupAddressLng'),
    email: getValue(values, 'pickupEmail'),
    resolvedBy: getValue(values, 'pickupResolvedBy'),
    pupUnitId: getValue(values, 'pickupPupUnitId') || null
  })
}

export const getIndicatorText = (actionType: OrderAction) => {
  switch (actionType) {
    case OrderAction.Copy:
      return 'instant.booking.copy'
    case OrderAction.Edit:
      return 'instant.booking.update'
    case OrderAction.Create:
      return 'instant.booking.create'
    case OrderAction.Restore:
      return 'restore'
  }
}

export const isEditOrRestore = (actionType: OrderAction) =>
  actionType == OrderAction.Edit || actionType == OrderAction.Restore

export const isEdit = (actionType: OrderAction) => actionType == OrderAction.Edit

const createDangerousGoodsSet = (
  dangerousGoods: List<DangerousGoods | DangerousGoodsFormikProps> | undefined = List([])
) => {
  return toPlainArray(dangerousGoods).map((goods) => ({
    regulationCode: getValue(goods, 'regulationCode'),
    undgHazardIdentifier: getValue(goods, 'hazardIdentifier'),
    hazardClass: getValue(goods, 'hazardClass'),
    packingGroup: getValue(goods, 'packingGroup'),
    technicalName: getValue(goods, 'technicalName'),
    hazardPoints: getValue(goods, 'hazardPoints'),
    measures: [
      {
        first: 'HazardNetWeight',
        second: {
          unit: 'KGM',
          value: getValue(goods, 'netWeightKg')
        }
      }
    ],
    restrictions: getValue(goods, 'restrictions')
  }))
}

export const createConsignmentSet = (
  values: IOrderForm | IOrderFormFormikValues,
  shipmentId: string,
  packageIds: number[],
  service: IOptimusService | null,
  pickupPhone: string,
  pickupSecondPhone: string | null,
  pickup: ImmutableMap<GroupRecipientProps>,
  deliveryPhone: string,
  deliverySecondPhone: string | null,
  recipient: ImmutableMap<GroupRecipientProps>,
  groupIndex: number,
  legSeqNumber: number | null | undefined,
  multilegId: number | null,
  groupId: string | null,
  groupName: string | null,
  orderAction: OrderAction,
  consignments: Seq.Indexed<Consignment> | undefined,
  orderType: OrderType | null,
  scanToCreatePackages: boolean,
  order?: Order,
  dangerousGoods?: List<DangerousGoods | DangerousGoodsFormikProps>
) => {
  const serviceVasCode = service?.get('vasCode')
  const valueAddedServices = serviceVasCode ? List([newVAS(serviceVasCode, '1')]) : List()
  const pureValueAddedServices = valueAddedServices.concat(
    List(toPlainArray(getValue(values, 'additionalServices')).map((vas) => Map(vas) as ValueAddedService))
      .filter((s) => s.get('vasCode'))
      .map(toPureVas)
  )
  const measurements = toPlainArray(getValue(values, 'measurements'))
  const customerParts = getCustomerSubCustomer(getValue(values, 'customerId'))
  const subcustomer = getValue(values, 'subcustomer') || customerParts.subcustomer
  const emailRecipients = {
    emailId: getValue(values, 'emailId'),
    createdEvent: getValue(values, 'createdEvent'),
    collectedEvent: getValue(values, 'collectedEvent'),
    deliveredEvent: getValue(values, 'deliveredEvent')
  }
  const fixedOrder = getValue(values, 'fixedOrder') || false
  const deliveryDateLatest = getDeliveryDateLatest(
    getValue(values, 'deliveryTimeEarliest'),
    getValue(values, 'deliveryTimeLatest'),
    getValue(values, 'pickupDate')
  )
  const nextPackageId = () => {
    const next = packageIds.pop()
    // all scanToCreatePackages should be placeholder-packages
    return scanToCreatePackages ? `temp-${next}` : `${next}`
  }
  const consignment = consignments?.get(0)

  return {
    updateIndicator: getIndicatorText(orderAction),
    legSeqNumber,
    multilegId,
    overriddenDepartmentId: getValue(values, 'selectedDepartmentId') || null,
    shipmentId,
    orderId: order?.get('id'),
    groupId,
    groupName,
    groupOrder: isEditOrRestore(orderAction) ? getValue(values, 'groupOrder') : fixedOrder ? groupIndex : null,
    paidTogether: getValue(values, 'paidTogether'),
    vehicleType: getValue(values, 'vehicleType'),
    emailRecipients: isEmpty(emailRecipients.emailId) ? undefined : List([emailRecipients]),
    note: getValue(values, 'orderNote'),
    controlTotals: {
      weight: { value: getMeasurementsTotalWeight(measurements), unit: MeasurementUnit.KGM },
      volume: { value: getMeasurementsTotalVolume(measurements), unit: MeasurementUnit.DMQ },
      dimensions: {
        height: { value: getMeasurementsTotalHeight(measurements), unit: MeasurementUnit.CMT },
        length: { value: getMeasurementsTotalLength(measurements), unit: MeasurementUnit.CMT },
        width: { value: getMeasurementsTotalWidth(measurements), unit: MeasurementUnit.CMT }
      },
      loadingMeters: { value: getMeasurementsTotalLoadingMeters(measurements), unit: MeasurementUnit.MTR },
      palletFootPrints: getMeasurementsTotalPalletFootPrints(measurements)
    },
    // Edits are sent as 'glow', so glow-orders needs the type to keep MyBooking orders as Pickup
    orderType: orderType ?? (isEdit(orderAction) && order?.get('type') === 'Pickup' ? 'Pickup' : null),
    dangerousGoods: createDangerousGoodsSet(dangerousGoods),
    correctedArticlePrices: getValue(values, 'correctedArticlePrices'),
    allowIncompletePrice: getValue(values, 'allowIncompletePrice'),
    hasZeroPriceApproval: getValue(values, 'hasZeroPriceApproval'),
    muteBomArticles: getValue(values, 'muteBomArticles'),
    needsZeroPriceApproval: getValue(values, 'needsZeroPriceApproval'),
    calculatedArticlePrices: getValue(values, 'calculatedArticlePrices'),
    consignments: measurements.map((measurement) => ({
      serviceCode: service?.get('serviceCode'),
      alystraServiceCode: service?.get('alystraServiceCode'),
      additionalServiceCodes: pureValueAddedServices.toJS(),
      packageId: isEditOrRestore(orderAction) ? getValue(measurement, 'packageId') || nextPackageId() : nextPackageId(),
      referenceNo: getValue(values, 'customerRef'),
      recipientRef: recipient.get('reference'),
      packageTypeCode: consignments
        ?.find((c) => c.get('packageId') === getValue(measurement, 'packageId'))
        ?.get('packageTypeCode'),
      customerTotalPrice: consignment?.get('customerTotalPrice'),
      carrierTotalPrice: consignment?.get('carrierTotalPrice'),
      customerContact: getValue(values, 'customerContact'),
      consignor: {
        partyId: customerParts.customerNumber,
        alystraPartyId: customerParts.customerNumber,
        subPartyId: subcustomer,
        name: pickup.get('name'),
        address: pickup.get('address'),
        city: pickup.get('city'),
        postalCode: trim(pickup.get('zipCode')),
        countryCode: pickup.get('countryCode'),
        phone: pickupSecondPhone,
        cellPhone: pickupPhone,
        contactPerson: pickup.get('contactPerson'),
        email: pickup.get('email'),
        lat: pickup.get('lat'),
        lng: pickup.get('lng'),
        resolvedBy: pickup.get('resolvedBy'),
        pupUnitId: pickup.get('pupUnitId')
      },
      consignee: {
        name: recipient.get('name'),
        address: recipient.get('address'),
        city: recipient.get('city'),
        postalCode: trim(recipient.get('zipCode')),
        countryCode: recipient.get('countryCode'),
        phone: deliverySecondPhone,
        cellPhone: deliveryPhone,
        contactPerson: recipient.get('contactPerson'),
        email: recipient.get('email'),
        lat: recipient.get('lat'),
        lng: recipient.get('lng'),
        resolvedBy: recipient.get('resolvedBy'),
        pupUnitId: recipient.get('pupUnitId')
      },
      preplannedUnitId: getValue(values, 'preplannedUnitId'),
      preplannedTemplateSlotId: toNumberOrNull(getValue(values, 'preplannedTemplateSlotId')),
      pickupDateEarliest: bookingFormatLocalDateTime(
        getValue(values, 'pickupDate'),
        getValue(values, 'pickupTimeEarliest')
      ),
      pickupDateLatest: bookingFormatLocalDateTime(
        getValue(values, 'pickupDate'),
        getValue(values, 'pickupTimeLatest')
      ),
      pickupInstructions: pickup.get('instructions') || '',
      deliveryDateEarliest: bookingFormatLocalDateTime(
        getValue(values, 'pickupDate'),
        getValue(values, 'deliveryTimeEarliest')
      ),
      // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
      deliveryDateLatest: bookingFormatLocalDateTime(deliveryDateLatest, getValue(values, 'deliveryTimeLatest')),
      deliveryInstructions: recipient.get('instructions') || '',
      specificationDescription: getValue(measurement, 'specificationDescription'),
      manualInvoiceInformation: getValue(values, 'manualInvoiceInfo'),
      invoiceInformation: getValue(values, 'invoiceInfo'),
      packageMeasures: {
        presetDimension: getValue(measurement, 'presetDimension'),
        weight: { value: getValue(measurement, 'weight'), unit: MeasurementUnit.KGM },
        volume: { value: getMeasurementVolume(measurement), unit: MeasurementUnit.DMQ },
        dimensions: isAnyDimensionNull(measurement)
          ? null
          : {
              height: { value: getValue(measurement, 'height'), unit: MeasurementUnit.CMT },
              length: { value: getValue(measurement, 'length'), unit: MeasurementUnit.CMT },
              width: { value: getValue(measurement, 'width'), unit: MeasurementUnit.CMT }
            },
        palletFootPrints: getValue(measurement, 'palletFootPrints') ?? undefined,
        loadingMeters: getValue(measurement, 'loadingMeters')
          ? { value: getValue(measurement, 'loadingMeters'), unit: MeasurementUnit.MTR }
          : undefined
      }
    })),
    invoiceSplit: getValue(values, 'invoiceSplit') === '' ? null : getValue(values, 'invoiceSplit'),
    standaloneInvoice: getValue(values, 'standaloneInvoice')
  }
}

function isAnyDimensionNull(measurement: IMeasurement | IOrderFormFormikValues['measurements'][number]): boolean {
  return (
    isEmpty(getValue(measurement, 'length')) ||
    isEmpty(getValue(measurement, 'width')) ||
    isEmpty(getValue(measurement, 'height'))
  )
}

function toNumberOrNull(value: string): number | null {
  const parsed = Number(value)
  return value === '' || isNaN(parsed) ? null : parsed
}

export const sortByName = (a: Map<string, any>, b: Map<string, any>) => {
  const first = a.get('name', '')?.toLowerCase()
  const second = b.get('name', '')?.toLowerCase()
  return first > second ? 1 : second > first ? -1 : 0
}

export const sortByRankAndName = (a: Map<string, any>, b: Map<string, any>) => {
  // Descending by rank
  const rankA = Number.parseFloat(a.get('rank', 0))
  const rankB = Number.parseFloat(b.get('rank', 0))
  if (rankA !== rankB) {
    return rankB > rankA ? 1 : rankB < rankA ? -1 : 0
  }

  // Ascending by name
  const nameA = (a.get('name', '') || '').toLowerCase()
  const nameB = (b.get('name', '') || '').toLowerCase()
  return nameA > nameB ? 1 : nameA < nameB ? -1 : 0
}

export const prepareClearComponentMap = (component: string, department: Department) => {
  const res = Map<string, any>()
  if (component === 'pickupPhone' || component === 'deliveryPhone') {
    return res.set(component, getPhonePrefixFromDepartment(department))
  } else if (component === 'measurements') {
    return res.set(
      component,
      getAvailableMeasurements(department)
        .filter((row: ISize) => row.get('id') == 0)
        .map(sizeToMeasurement)
    )
  } else if (component === 'additionalServices') {
    return res.delete(component).updateIn([component], () => List([emptyVAS()]))
  }
  return res.set(component, '')
}

const sizeToMeasurement = (size: ISize): IMeasurement =>
  Map({
    weight: size.get('weight'),
    height: size.get('height'),
    length: size.get('length'),
    width: size.get('width'),
    presetDimension: size.get('presetDimension'),
    specificationDescription: '',
    volume: '',
    loadingMeters: size.get('loadingMeters'),
    palletFootPrints: size.get('palletFootPrints'),
    quantity: '1'
  })
